import { useCallback, useEffect, useMemo, useState, type FormEvent, type Key } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import DataTable, { type DataTableColumn } from '../components/common/DataTable';
import { blockBackdropClose, FormDialogTitle, LoadingState } from '../components/common';
import { sendPasswordResetEmail } from '../services/authService';
import {
  requestCheckManagerAccountDuplicate,
  requestCheckManagerEmailDuplicate,
  requestCreateManager,
  requestDeleteManagers,
  requestManagers,
  requestMyManagerProfile,
  requestUpdateManager,
  type ManagerAccount,
  type ManagerCreateRequest,
  type ManagerProfileResponse,
  type ManagerRole,
  type ManagerUpdateRequest,
} from '../api/managerApi';
import { formatMobilePhoneNumber, isValidMobilePhoneNumber } from '../utils/phoneNumber';

type AccountFormState = {
  account: string;
  contactNumber: string;
  email: string;
  role: ManagerRole;
};

type DuplicateCheckState = 'idle' | 'checking' | 'available' | 'duplicated';

const emptyForm: AccountFormState = {
  account: '',
  contactNumber: '',
  email: '',
  role: 'GENERAL',
};

const PAGE_SIZE = 20;

const ROLE_OPTIONS: { label: string; value: ManagerRole }[] = [
  { label: '슈퍼 관리자', value: 'SUPER' },
  { label: '중간 관리자', value: 'MIDDLE' },
  { label: '일반 관리자', value: 'GENERAL' },
  { label: '일반 사용자', value: 'NORMAL' },
];

const CREATABLE_ROLE_OPTIONS = ROLE_OPTIONS.filter((option) => option.value !== 'SUPER');

const ROLE_HELP_ITEMS: { role: ManagerRole; scope: string; note?: string }[] = [
  { role: 'SUPER', scope: '모든 API' },
  { role: 'MIDDLE', scope: 'SUPER에서 사용자관리 API 제외' },
  {
    role: 'GENERAL',
    scope: '대회 기능쪽의 모든 조회 기능 가능, 참가자 정보 수정, 결과 수정 가능',
    note: '대회 진행 중 도움을 주는 관리자',
  },
  {
    role: 'NORMAL',
    scope: '대회 기능 쪽의 일부 조회 기능 사용가능(통계, 결과, 참가자, 대회정보)',
    note: '관련 업체(주최사, 배송사 등)에서 상황 또는 결과 조회 용도의 권한',
  },
];

const InfoIcon = () => (
  <svg
    width={15}
    height={15}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const accountPattern = /^[A-Za-z0-9]{5,15}$/;

function isValidEmail(email: string) {
  const atIndex = email.indexOf('@');
  if (atIndex <= 0 || atIndex !== email.lastIndexOf('@')) {
    return false;
  }

  const domain = email.slice(atIndex + 1);
  const lastDotIndex = domain.lastIndexOf('.');
  return (
    lastDotIndex > 0
    && lastDotIndex < domain.length - 1
    && email.split('').every((character) => character.trim() !== '')
  );
}

function getRoleLabel(role: string) {
  return ROLE_OPTIONS.find((option) => option.value === role)?.label ?? role;
}

const roleHelpTooltipContent = (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, p: 0.5 }}>
    {ROLE_HELP_ITEMS.map((item) => (
      <Box key={item.role}>
        <Box component="span" sx={{ fontWeight: 700, fontSize: 12 }}>
          {getRoleLabel(item.role)} ({item.role})
        </Box>
        <Box sx={{ fontSize: 12, mt: 0.25 }}>{item.scope}</Box>
        {item.note && (
          <Box sx={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', mt: 0.25 }}>
            비고: {item.note}
          </Box>
        )}
      </Box>
    ))}
  </Box>
);

function normalizeContactNumber(value?: string) {
  return value?.trim() ? formatMobilePhoneNumber(value) : '-';
}

function toManagerIds(selectedIds: ReadonlyArray<Key>) {
  return selectedIds.map(Number).filter((id) => Number.isFinite(id));
}

function getDuplicateHelperText(
  state: DuplicateCheckState,
  availableMessage: string,
  duplicatedMessage: string
) {
  if (state === 'available') {
    return availableMessage;
  }

  if (state === 'duplicated') {
    return duplicatedMessage;
  }

  if (state === 'checking') {
    return '확인 중입니다.';
  }

  return '중복 확인이 필요합니다.';
}

function getDuplicateFieldSx(state: DuplicateCheckState) {
  if (state !== 'available') {
    return undefined;
  }

  return {
    '& .MuiFormHelperText-root': {
      color: '#15803d',
    },
    '& .MuiInputLabel-root': {
      color: '#15803d',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#15803d',
    },
    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
      borderColor: '#22c55e',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#16a34a',
      borderWidth: 2,
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#16a34a',
    },
  };
}

function UserManagementPage() {
  const [managers, setManagers] = useState<ManagerAccount[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [keyword, setKeyword] = useState('');
  const [appliedKeyword, setAppliedKeyword] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [appliedRole, setAppliedRole] = useState('');
  const [selectedIds, setSelectedIds] = useState<Key[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [passwordResetSending, setPasswordResetSending] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [form, setForm] = useState<AccountFormState>(emptyForm);
  const [editForm, setEditForm] = useState<AccountFormState>(emptyForm);
  const [activeManager, setActiveManager] = useState<ManagerAccount | null>(null);
  const [currentManager, setCurrentManager] = useState<ManagerProfileResponse | null>(null);
  const [accountCheckState, setAccountCheckState] = useState<DuplicateCheckState>('idle');
  const [emailCheckState, setEmailCheckState] = useState<DuplicateCheckState>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [modalErrorMessage, setModalErrorMessage] = useState('');
  const [modalSuccessMessage, setModalSuccessMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const selectedManagers = useMemo(() => {
    const selectedIdSet = new Set(selectedIds.map(String));
    return managers.filter((manager) => selectedIdSet.has(String(manager.id)));
  }, [managers, selectedIds]);
  const canDeleteManagers = currentManager?.role === 'SUPER';
  const canUpdateManagers = currentManager?.role === 'SUPER';
  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const columns: DataTableColumn<ManagerAccount>[] = [
    {
      key: 'account',
      header: '아이디',
      sortable: true,
      width: 160,
      render: (manager) => (
        <Box sx={{ color: '#111827', fontWeight: 700 }}>{manager.account}</Box>
      ),
    },
    {
      key: 'role',
      header: '권한',
      sortable: true,
      width: 140,
      render: (manager) => (
        <Chip
          label={getRoleLabel(manager.role)}
          size="small"
          sx={{
            backgroundColor: manager.role === 'SUPER' ? '#fef2f2' : '#eef6ff',
            color: manager.role === 'SUPER' ? '#b91c1c' : '#0f5ea8',
            fontSize: 11,
            fontWeight: 700,
          }}
        />
      ),
    },
    {
      key: 'contactNumber',
      header: '전화번호',
      sortable: true,
      width: 160,
      render: (manager) => normalizeContactNumber(manager.contactNumber),
    },
    {
      key: 'email',
      header: '이메일',
      sortable: true,
      width: 240,
    },
  ];

  const loadManagers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await requestManagers(appliedKeyword, appliedRole, page, PAGE_SIZE);
      setManagers(response.managers);
      setTotalCount(response.totalCount);
      setSelectedIds([]);
      setErrorMessage('');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '계정 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [appliedKeyword, appliedRole, page]);

  useEffect(() => {
    void loadManagers();
  }, [loadManagers]);

  useEffect(() => {
    requestMyManagerProfile()
      .then(setCurrentManager)
      .catch(() => {
        setCurrentManager(null);
      });
  }, []);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPage(0);
    setAppliedKeyword(keyword.trim());
    setAppliedRole(roleFilter);
  };

  const handleResetSearch = () => {
    setKeyword('');
    setRoleFilter('');
    setAppliedKeyword('');
    setAppliedRole('');
    setPage(0);
  };

  const resetDialog = () => {
    setForm(emptyForm);
    setAccountCheckState('idle');
    setEmailCheckState('idle');
    setModalErrorMessage('');
    setModalSuccessMessage('');
  };

  const openCreateDialog = () => {
    resetDialog();
    setDialogOpen(true);
    setErrorMessage('');
    setSuccessMessage('');
  };

  const closeCreateDialog = () => {
    if (!saving) {
      setDialogOpen(false);
      resetDialog();
    }
  };

  const openDetailDialog = (manager: ManagerAccount) => {
    if (!canUpdateManagers || manager.role === 'SUPER') {
      return;
    }

    setActiveManager(manager);
    setEditForm({
      account: manager.account,
      contactNumber: formatMobilePhoneNumber(manager.contactNumber ?? ''),
      email: manager.email,
      role: manager.role,
    });
    setModalErrorMessage('');
    setModalSuccessMessage('');
    setDetailDialogOpen(true);
    setErrorMessage('');
    setSuccessMessage('');
  };

  const closeDetailDialog = () => {
    if (!updating && !passwordResetSending) {
      setDetailDialogOpen(false);
      setActiveManager(null);
      setEditForm(emptyForm);
      setModalErrorMessage('');
      setModalSuccessMessage('');
    }
  };

  const handleCheckAccountDuplicate = async () => {
    const account = form.account.trim();

    if (!accountPattern.test(account)) {
      setAccountCheckState('idle');
      setModalErrorMessage('아이디는 영문 또는 숫자 5~15자로 입력해 주세요.');
      return;
    }

    setAccountCheckState('checking');
    try {
      const response = await requestCheckManagerAccountDuplicate(account);
      setAccountCheckState(response.isDuplicated ? 'duplicated' : 'available');
      setModalErrorMessage(response.isDuplicated ? '이미 사용 중인 아이디입니다.' : '');
    } catch (error) {
      setAccountCheckState('idle');
      setModalErrorMessage(error instanceof Error ? error.message : '아이디 중복 확인에 실패했습니다.');
    }
  };

  const handleCheckEmailDuplicate = async () => {
    const email = form.email.trim();

    if (!isValidEmail(email)) {
      setEmailCheckState('idle');
      setModalErrorMessage('올바른 이메일 형식으로 입력해 주세요.');
      return;
    }

    setEmailCheckState('checking');
    try {
      const response = await requestCheckManagerEmailDuplicate(email);
      setEmailCheckState(response.isDuplicated ? 'duplicated' : 'available');
      setModalErrorMessage(response.isDuplicated ? '이미 사용 중인 이메일입니다.' : '');
    } catch (error) {
      setEmailCheckState('idle');
      setModalErrorMessage(error instanceof Error ? error.message : '이메일 중복 확인에 실패했습니다.');
    }
  };

  const validateForm = () => {
    if (!accountPattern.test(form.account.trim())) {
      return '아이디는 영문 또는 숫자 5~15자로 입력해 주세요.';
    }

    if (!form.role) {
      return '권한을 선택해 주세요.';
    }

    if (!isValidEmail(form.email.trim())) {
      return '올바른 이메일 형식으로 입력해 주세요.';
    }

    if (!isValidMobilePhoneNumber(form.contactNumber.trim())) {
      return '전화번호를 010-1234-5678 형식으로 입력해 주세요.';
    }

    if (accountCheckState !== 'available') {
      return '아이디 중복 확인을 완료해 주세요.';
    }

    if (emailCheckState !== 'available') {
      return '이메일 중복 확인을 완료해 주세요.';
    }

    return '';
  };

  const validateEditForm = () => {
    if (!editForm.role) {
      return '권한을 선택해 주세요.';
    }

    if (!isValidMobilePhoneNumber(editForm.contactNumber.trim())) {
      return '전화번호를 010-1234-5678 형식으로 입력해 주세요.';
    }

    return '';
  };

  const handleCreateAccount = async () => {
    const validationMessage = validateForm();

    if (validationMessage) {
      setModalErrorMessage(validationMessage);
      return;
    }

    const payload: ManagerCreateRequest = {
      account: form.account.trim(),
      contactNumber: formatMobilePhoneNumber(form.contactNumber) || undefined,
      email: form.email.trim(),
      role: form.role,
    };

    setSaving(true);
    try {
      await requestCreateManager(payload);
      await loadManagers();
      setDialogOpen(false);
      resetDialog();
      setSuccessMessage('계정이 등록되었습니다.');
      setErrorMessage('');
    } catch (error) {
      setModalErrorMessage(error instanceof Error ? error.message : '계정 등록에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateAccount = async () => {
    if (!activeManager) {
      return;
    }

    const validationMessage = validateEditForm();

    if (validationMessage) {
      setModalErrorMessage(validationMessage);
      return;
    }

    const payload: ManagerUpdateRequest = {
      contactNumber: formatMobilePhoneNumber(editForm.contactNumber) || undefined,
      email: activeManager.email,
      role: editForm.role,
    };

    setUpdating(true);
    try {
      await requestUpdateManager(activeManager.id, payload);
      await loadManagers();
      setDetailDialogOpen(false);
      setActiveManager(null);
      setSuccessMessage('계정 정보가 수정되었습니다.');
      setModalErrorMessage('');
      setErrorMessage('');
    } catch (error) {
      setModalErrorMessage(error instanceof Error ? error.message : '계정 수정에 실패했습니다.');
    } finally {
      setUpdating(false);
    }
  };

  const handleSendPasswordResetEmail = async () => {
    if (!activeManager) {
      return;
    }

    const email = activeManager.email.trim();

    if (!isValidEmail(email)) {
      setModalSuccessMessage('');
      setModalErrorMessage('올바른 이메일이 등록된 사용자만 비밀번호 초기화 메일을 발송할 수 있습니다.');
      return;
    }

    setPasswordResetSending(true);
    setModalErrorMessage('');
    setModalSuccessMessage('');

    try {
      await sendPasswordResetEmail(email);
      setModalSuccessMessage('비밀번호 초기화 메일을 발송했습니다.');
    } catch (error) {
      setModalErrorMessage(error instanceof Error ? error.message : '비밀번호 초기화 메일 발송에 실패했습니다.');
    } finally {
      setPasswordResetSending(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) {
      setErrorMessage('삭제할 계정을 선택해 주세요.');
      return;
    }

    if (!canDeleteManagers) {
      setErrorMessage('계정 삭제는 슈퍼 관리자만 가능합니다.');
      return;
    }

    if (selectedManagers.some((manager) => manager.role === 'SUPER')) {
      setErrorMessage('슈퍼 관리자 계정은 삭제할 수 없습니다.');
      return;
    }

    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deleting) {
      return;
    }

    setDeleteDialogOpen(false);
  };

  const confirmDeleteSelected = async () => {

    const managerIds = toManagerIds(selectedIds);

    setDeleting(true);
    try {
      await requestDeleteManagers(managerIds);
      await loadManagers();
      setSelectedIds([]);
      setDeleteDialogOpen(false);
      setSuccessMessage('선택한 계정이 삭제되었습니다.');
      setErrorMessage('');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '계정 삭제에 실패했습니다.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <section className="page-placeholder">
      <div className="page-content page-content-wide">
        <div className="page-title-block">
          <p className="page-section">Admin</p>
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
            <h1>사용자 관리</h1>
            <Tooltip
              title={roleHelpTooltipContent}
              componentsProps={{ tooltip: { sx: { maxWidth: 360 } } }}
            >
              <IconButton
                size="small"
                aria-label="권한별 접근 범위 안내"
                sx={{ color: '#94a3b8', '&:hover': { color: '#1da1f2' } }}
              >
                <InfoIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <p className="page-description">관리자 계정을 조회하고 권한과 연락처를 관리합니다.</p>
        </div>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'flex-end',
            mb: 3,
          }}
        >
          <Button onClick={openCreateDialog} variant="contained">
            계정 추가
          </Button>
        </Box>

        {errorMessage && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
            {errorMessage}
          </Alert>
        )}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
            {successMessage}
          </Alert>
        )}

        <Paper
          variant="outlined"
          sx={{
            borderColor: '#e2e8f0',
            borderRadius: 1,
            boxShadow: 'none',
            mb: 2,
            p: 2,
          }}
        >
          <Box
            sx={{
              alignItems: { xs: 'stretch', sm: 'center' },
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1,
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ color: '#475569', fontSize: 13, fontWeight: 700 }}>
              총 {totalCount}개 계정
            </Box>
            <Button
              color="error"
              disabled={deleting || selectedIds.length === 0 || !canDeleteManagers}
              onClick={handleDeleteSelected}
              variant="outlined"
            >
              {deleting ? '삭제 중' : `선택 삭제 (${selectedIds.length})`}
            </Button>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ borderColor: '#e2e8f0', boxShadow: 'none', mb: 2, p: 2 }}>
          <Box component="form" onSubmit={handleSearch} sx={{ alignItems: { xs: 'stretch', md: 'center' }, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 1 }}>
            <TextField inputProps={{ maxLength: 100 }} label="아이디 검색" onChange={(event) => setKeyword(event.target.value)} size="small" sx={{ flex: 1, minWidth: 0 }} value={keyword} />
            <TextField InputLabelProps={{ shrink: true }} label="권한" onChange={(event) => setRoleFilter(event.target.value)} select SelectProps={{ native: true }} size="small" sx={{ minWidth: { xs: '100%', md: 180 } }} value={roleFilter}>
              <option value="">전체 권한</option>
              {ROLE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </TextField>
            <Button disabled={loading} sx={{ flexShrink: 0, minWidth: 72 }} type="submit" variant="contained">검색</Button>
            <Button disabled={loading} onClick={handleResetSearch} sx={{ flexShrink: 0, minWidth: 72 }} type="button" variant="outlined">초기화</Button>
          </Box>
        </Paper>

        {loading ? (
          <LoadingState message="계정 목록을 불러오는 중입니다." />
        ) : (
          <>
            <DataTable
              columns={columns}
              emptyMessage={appliedKeyword || appliedRole ? '검색 조건에 맞는 계정이 없습니다.' : '등록된 계정이 없습니다.'}
              onRowClick={canUpdateManagers ? openDetailDialog : undefined}
              onSelectionChange={setSelectedIds}
              rowKey="id"
              rows={managers}
              selectable
              selectedRowKeys={selectedIds}
            />
            {pageCount > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination count={pageCount} onChange={(_, nextPage) => setPage(nextPage - 1)} page={page + 1} shape="rounded" />
              </Box>
            )}
          </>
        )}
      </div>

      <Dialog fullWidth maxWidth="xs" onClose={closeDeleteDialog} open={deleteDialogOpen}>
        <DialogTitle sx={{ fontSize: 20, fontWeight: 800 }}>계정 삭제</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ color: '#374151', fontSize: 14, lineHeight: 1.7 }}>
            선택한 계정 {selectedIds.length}건을 삭제할까요?
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button disabled={deleting} onClick={closeDeleteDialog} variant="outlined">
            취소
          </Button>
          <Button color="error" disabled={deleting} onClick={confirmDeleteSelected} variant="contained">
            {deleting ? '삭제 중...' : '삭제'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog fullWidth maxWidth="sm" onClose={blockBackdropClose(closeCreateDialog)} open={dialogOpen}>
        <FormDialogTitle onClose={closeCreateDialog} sx={{ fontSize: 20, fontWeight: 800 }}>
          계정 등록
        </FormDialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 2, pt: 0.5 }}>
            {modalErrorMessage && (
              <Alert severity="warning" onClose={() => setModalErrorMessage('')}>
                {modalErrorMessage}
              </Alert>
            )}
            <TextField
              error={accountCheckState === 'duplicated'}
              helperText={getDuplicateHelperText(
                accountCheckState,
                '사용 가능한 아이디입니다.',
                '이미 사용 중인 아이디입니다.'
              )}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      disabled={accountCheckState === 'checking'}
                      onClick={handleCheckAccountDuplicate}
                      size="small"
                      type="button"
                    >
                      중복 확인
                    </Button>
                  </InputAdornment>
                ),
              }}
              label="아이디"
              onChange={(event) => {
                setForm((current) => ({ ...current, account: event.target.value }));
                setAccountCheckState('idle');
                setModalErrorMessage('');
              }}
              required
              sx={getDuplicateFieldSx(accountCheckState)}
              value={form.account}
            />
            <TextField
              label="권한"
              onChange={(event) => {
                setForm((current) => ({ ...current, role: event.target.value as ManagerRole }));
                setModalErrorMessage('');
              }}
              required
              select
              SelectProps={{ native: true }}
              value={form.role}
            >
              {CREATABLE_ROLE_OPTIONS.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </TextField>
            <TextField
              helperText="예: 010-1234-5678"
              inputProps={{ inputMode: 'numeric', maxLength: 13 }}
              label="전화번호"
              onChange={(event) => {
                setForm((current) => ({
                  ...current,
                  contactNumber: formatMobilePhoneNumber(event.target.value),
                }));
                setModalErrorMessage('');
              }}
              value={form.contactNumber}
            />
            <TextField
              error={emailCheckState === 'duplicated'}
              helperText={getDuplicateHelperText(
                emailCheckState,
                '사용 가능한 이메일입니다.',
                '이미 사용 중인 이메일입니다.'
              )}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      disabled={emailCheckState === 'checking'}
                      onClick={handleCheckEmailDuplicate}
                      size="small"
                      type="button"
                    >
                      중복 확인
                    </Button>
                  </InputAdornment>
                ),
              }}
              label="이메일"
              onChange={(event) => {
                setForm((current) => ({ ...current, email: event.target.value }));
                setEmailCheckState('idle');
                setModalErrorMessage('');
              }}
              required
              sx={getDuplicateFieldSx(emailCheckState)}
              type="email"
              value={form.email}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button disabled={saving} onClick={closeCreateDialog} variant="outlined">
            취소
          </Button>
          <Button disabled={saving} onClick={handleCreateAccount} variant="contained">
            {saving ? '등록 중' : '등록'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog fullWidth maxWidth="sm" onClose={blockBackdropClose(closeDetailDialog)} open={detailDialogOpen}>
        <FormDialogTitle onClose={closeDetailDialog} sx={{ fontSize: 20, fontWeight: 800 }}>
          계정 상세
        </FormDialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 2, pt: 0.5 }}>
            {modalErrorMessage && (
              <Alert severity="warning" onClose={() => setModalErrorMessage('')}>
                {modalErrorMessage}
              </Alert>
            )}
            {modalSuccessMessage && (
              <Alert severity="success" onClose={() => setModalSuccessMessage('')}>
                {modalSuccessMessage}
              </Alert>
            )}
            <TextField
              InputProps={{ readOnly: true }}
              label="아이디"
              value={editForm.account}
            />
            <TextField
              label="권한"
              onChange={(event) => {
                setEditForm((current) => ({ ...current, role: event.target.value as ManagerRole }));
                setModalErrorMessage('');
                setModalSuccessMessage('');
              }}
              required
              select
              SelectProps={{ native: true }}
              value={editForm.role}
            >
              {CREATABLE_ROLE_OPTIONS.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </TextField>
            <TextField
              helperText="예: 010-1234-5678"
              inputProps={{ inputMode: 'numeric', maxLength: 13 }}
              label="전화번호"
              onChange={(event) => {
                setEditForm((current) => ({
                  ...current,
                  contactNumber: formatMobilePhoneNumber(event.target.value),
                }));
                setModalErrorMessage('');
                setModalSuccessMessage('');
              }}
              value={editForm.contactNumber}
            />
            <TextField
              InputProps={{ readOnly: true }}
              label="이메일"
              required
              type="email"
              value={editForm.email}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            color="warning"
            disabled={updating || passwordResetSending || !activeManager?.email}
            onClick={handleSendPasswordResetEmail}
            variant="outlined"
          >
            {passwordResetSending ? '발송 중...' : '비밀번호 초기화'}
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button disabled={updating || passwordResetSending} onClick={closeDetailDialog} variant="outlined">
            닫기
          </Button>
          <Button disabled={updating || passwordResetSending} onClick={handleUpdateAccount} variant="contained">
            {updating ? '수정 중' : '수정'}
          </Button>
        </DialogActions>
      </Dialog>
    </section>
  );
}

export default UserManagementPage;
